import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, CalendarDays, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6];

export default function StoreClosedDays() {
  const { t } = useTranslation();
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/stores")
      .then(({ data }) => {
        setStores(data);
        if (data.length) setStoreId(data[0].id);
      })
      .catch(() => toast.error(t("storeClosedDays.loadFailed")))
      .finally(() => setLoading(false));
  }, [t]);

  const selectedStore = useMemo(
    () => stores.find((store) => store.id === storeId),
    [stores, storeId]
  );

  useEffect(() => {
    setDays([...(selectedStore?.closed_weekdays || [])].sort((a, b) => a - b));
  }, [selectedStore]);

  const toggleDay = (day) => {
    setDays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day].sort((a, b) => a - b)
    );
  };

  const save = async () => {
    if (!storeId) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/stores/${storeId}`, {
        closed_weekdays: days,
      });
      setStores((current) =>
        current.map((store) => (store.id === storeId ? { ...store, ...data } : store))
      );
      toast.success(t("storeClosedDays.saved"));
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || t("storeClosedDays.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="pg-card p-10 text-center text-slate-400">{t("common.loading")}</div>;
  }

  return (
    <div data-testid="store-closed-days-page">
      <Link to="/dashboard" className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-2 mb-4">
        <ArrowLeft size={15} /> {t("common.back")}
      </Link>

      <div className="mb-7">
        <div className="text-xs uppercase tracking-[0.32em] text-slate-400 mb-1">
          {t("storeClosedDays.breadcrumb")}
        </div>
        <h1 className="text-3xl font-extrabold text-slate-900">{t("storeClosedDays.title")}</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-3xl">{t("storeClosedDays.description")}</p>
      </div>

      <section className="pg-card p-6 lg:p-8 max-w-3xl">
        <div className="field-section-title mb-5">
          <CalendarDays size={16} /> {t("storeClosedDays.schedule")}
        </div>

        {stores.length === 0 ? (
          <div className="text-sm text-slate-500">{t("storeClosedDays.noStores")}</div>
        ) : (
          <div className="space-y-6">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                {t("storeClosedDays.store")}
              </label>
              <select
                className={inputClass}
                value={storeId}
                onChange={(event) => setStoreId(event.target.value)}
                data-testid="closed-days-store"
              >
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name} · MID {store.machine_id}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                {t("storeClosedDays.closedOn")}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {WEEKDAYS.map((day) => {
                  const selected = days.includes(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleDay(day)}
                      aria-pressed={selected}
                      className={`rounded-lg border px-4 py-3 text-sm font-semibold transition ${
                        selected
                          ? "border-amber-400 bg-amber-50 text-amber-800"
                          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                      }`}
                      data-testid={`closed-day-${day}`}
                    >
                      {t(`storeClosedDays.weekdays.${day}`)}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-3">{t("storeClosedDays.hint")}</p>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="px-5 py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60"
                data-testid="save-closed-days"
              >
                <Save size={15} /> {saving ? t("storeClosedDays.saving") : t("common.save")}
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

const inputClass =
  "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition bg-white text-sm";
